// Client-side password hashing for login using SHA-256 (no env values required)
// Falls back to a pure JS implementation if Web Crypto is unavailable

function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const hex: string[] = [];
  for (let i = 0; i < bytes.byteLength; i++) {
    const b = bytes[i].toString(16).padStart(2, "0");
    hex.push(b);
  }
  return hex.join("");
}

export async function encryptPasswordForLogin(password: string): Promise<{ encryptedBase64: string; algorithm: string } | null> {
  if (typeof window === "undefined") {
    return null;
  }

  // Prefer Web Crypto if available
  if (window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const encryptedBase64 = arrayBufferToHex(hashBuffer);
    return { encryptedBase64, algorithm: "sha-256-hex" };
  }

  // Fallback: pure JS SHA-256
  const encryptedBase64 = sha256Hex(password);
  return { encryptedBase64, algorithm: "sha-256-hex" };
}

// --- Minimal SHA-256 (hex) fallback ---
function sha256Hex(message: string): string {
  const bytes = utf8ToBytes(message);
  const h = new Uint32Array(8);
  h[0] = 0x6a09e667; h[1] = 0xbb67ae85; h[2] = 0x3c6ef372; h[3] = 0xa54ff53a;
  h[4] = 0x510e527f; h[5] = 0x9b05688c; h[6] = 0x1f83d9ab; h[7] = 0x5be0cd19;

  const k = new Uint32Array([
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2
  ]);

  const l = bytes.length;
  const withOne = l + 1;
  const kBytes = (withOne % 64 <= 56 ? 56 : 120) - (withOne % 64);
  const totalLen = withOne + kBytes + 8;
  const buffer = new Uint8Array(totalLen);
  buffer.set(bytes);
  buffer[l] = 0x80;
  const bitLenHi = Math.floor((l >>> 29) >>> 0);
  const bitLenLo = (l << 3) >>> 0;
  // Append length in bits (big-endian)
  buffer[totalLen - 8] = (bitLenHi >>> 24) & 0xff;
  buffer[totalLen - 7] = (bitLenHi >>> 16) & 0xff;
  buffer[totalLen - 6] = (bitLenHi >>> 8) & 0xff;
  buffer[totalLen - 5] = bitLenHi & 0xff;
  buffer[totalLen - 4] = (bitLenLo >>> 24) & 0xff;
  buffer[totalLen - 3] = (bitLenLo >>> 16) & 0xff;
  buffer[totalLen - 2] = (bitLenLo >>> 8) & 0xff;
  buffer[totalLen - 1] = bitLenLo & 0xff;

  const w = new Uint32Array(64);
  for (let i = 0; i < totalLen; i += 64) {
    for (let j = 0; j < 16; j++) {
      const idx = i + j * 4;
      w[j] = ((buffer[idx] << 24) | (buffer[idx + 1] << 16) | (buffer[idx + 2] << 8) | (buffer[idx + 3])) >>> 0;
    }
    for (let j = 16; j < 64; j++) {
      const s0 = rrot(w[j - 15], 7) ^ rrot(w[j - 15], 18) ^ (w[j - 15] >>> 3);
      const s1 = rrot(w[j - 2], 17) ^ rrot(w[j - 2], 19) ^ (w[j - 2] >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) >>> 0;
    }

    let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
    for (let j = 0; j < 64; j++) {
      const S1 = rrot(e, 6) ^ rrot(e, 11) ^ rrot(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (hh + S1 + ch + k[j] + w[j]) >>> 0;
      const S0 = rrot(a, 2) ^ rrot(a, 13) ^ rrot(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h[0] = (h[0] + a) >>> 0;
    h[1] = (h[1] + b) >>> 0;
    h[2] = (h[2] + c) >>> 0;
    h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0;
    h[5] = (h[5] + f) >>> 0;
    h[6] = (h[6] + g) >>> 0;
    h[7] = (h[7] + hh) >>> 0;
  }

  const out = new Uint8Array(32);
  for (let i = 0; i < 8; i++) {
    out[i * 4] = (h[i] >>> 24) & 0xff;
    out[i * 4 + 1] = (h[i] >>> 16) & 0xff;
    out[i * 4 + 2] = (h[i] >>> 8) & 0xff;
    out[i * 4 + 3] = h[i] & 0xff;
  }
  return bytesToHex(out);
}

function rrot(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

function utf8ToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

